/**
 * Buddy behavior toggles. Values come from VITE_BUDDY_* in .env.{dev|stg|prod}.
 */
const flag = (value: string | undefined, fallback: boolean) => {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return fallback;
};

const number = (value: string | undefined, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const list = (value: string | undefined, fallback: string[]) => {
  const names = value
    ?.split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return names?.length ? names : fallback;
};

export const BUDDY_CONFIG = {
  /** Show Buddy. Off in staging and production. */
  on: flag(import.meta.env.VITE_BUDDY, false),
  /** Grab and drag him. */
  move: flag(import.meta.env.VITE_BUDDY_MOVE, true),
  /** Glide toward header icons after a short hover. */
  icons: flag(import.meta.env.VITE_BUDDY_ICONS, true),
  /**
   * Which icons he may approach. Each name is the `data-buddy-icon` on that button.
   * Remove a name to stop him going there.
   */
  iconTargets: list(import.meta.env.VITE_BUDDY_ICON_TARGETS, [
    'invite',
    'incognito',
    'notifications',
    'create-agent',
  ]),
  /**
   * Path prefixes where Buddy stays hidden (denylist). `/login` is always hidden in code.
   * Includes checkout/payment plus unauthenticated auth routes.
   */
  hideRoutes: list(import.meta.env.VITE_BUDDY_HIDE_ROUTES, [
    '/checkout',
    '/payment',
    '/comingsoon',
    '/signup',
    '/sent-email',
    '/activate',
    '/resetpassword',
    '/success',
    '/activate-failed',
    '/forgot-password',
    '/verify-key',
    '/signup-email-sent',
    '/redirect',
    '/promotion',
    '/integrations/oauth-complete',
    '/composio/oauth/callback',
  ]),
  /** How long the pointer must stay on an icon before he moves, in milliseconds. */
  iconHoverMs: number(import.meta.env.VITE_BUDDY_ICON_HOVER_MS, 1000),
  /** How long a route or icon line must stay before he asks for a reply, in milliseconds. */
  replyMs: number(import.meta.env.VITE_BUDDY_REPLY_MS, 600),
  /** Tuck into the composer when that field is in use. */
  dock: flag(import.meta.env.VITE_BUDDY_DOCK, true),
  /** Say an AI line about the current page or hovered icon. */
  speak: flag(import.meta.env.VITE_BUDDY_SPEAK, true),
  /** Send icon on the speech bubble. Set false to hide it. */
  ask: flag(import.meta.env.VITE_BUDDY_ASK, true),
  /** Open the guided tour when asked ("Give me a tour"). Keep it on one Buddy only. */
  tour: flag(import.meta.env.VITE_BUDDY_TOUR, true),
  /** Show Buddy on phones (under 768px). */
  mobile: flag(import.meta.env.VITE_BUDDY_MOBILE, false),
  /** Show Buddy on tablets (768px to 1023px). */
  tablet: flag(import.meta.env.VITE_BUDDY_TABLET, false),
  /** Agent used when Buddy asks for a spoken reply. */
  agentId: import.meta.env.VITE_BUDDY_AGENT_ID?.trim() || '000000000000000000000000',
};

/**
 * What an inline Buddy (`<Buddy inline />`) does differently from the floating one: he only floats,
 * blinks and looks around. Turn a feature back on for one use with `config`, e.g. `{ speak: true }`.
 */
export const BUDDY_INLINE_CONFIG: Partial<typeof BUDDY_CONFIG> = {
  move: false,
  icons: false,
  dock: false,
  speak: false,
  ask: false,
  tour: false,
  /* The page placed him, so he shows on every screen size. */
  mobile: true,
  tablet: true,
};

/** True when Buddy should not render on this pathname (login + env denylist). */
export function isBuddyHiddenPath(pathname: string): boolean {
  const path = pathname || '';
  if (path === '/login' || path.startsWith('/login/')) return true;
  return BUDDY_CONFIG.hideRoutes.some(
    (route) => path === route || path.startsWith(`${route}/`)
  );
}
